import { Button } from "@material-ui/core";
import { CheckCircleFill } from '@styled-icons/bootstrap/CheckCircleFill';
import { FilePerson } from '@styled-icons/bootstrap/FilePerson'; // resume
import { BadgeCheck } from '@styled-icons/boxicons-regular/BadgeCheck'; // exp
import { LeftArrowAlt } from '@styled-icons/boxicons-regular/LeftArrowAlt';
import { Microphone } from '@styled-icons/boxicons-regular/Microphone';
import { RightArrowAlt } from '@styled-icons/boxicons-regular/RightArrowAlt';
import { AssignmentInd } from '@styled-icons/material-twotone/AssignmentInd'; // build profile
import React from "react";
import { useSelector } from "react-redux";
import { BounceLoader } from "react-spinners";
import { RootState } from "store";
import styled from "styled-components";
import { StyledIcon } from "styled-icons/types";

const StyledWrapper = styled.div`
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: space-around;
    padding: 1rem;
    gap: 0.5rem;

    .steps-container {
        height: 23%;
    }
    .steps-form-container {
        height: 70%;
        padding: 1rem 2rem 1rem 2rem;
        overflow: auto;
        align-content: center;
        position: relative;
    }
    .steps-form-container > div {
        width: 100%;
        max-width: 100rem;
        margin: auto;
    }
    .styled-box-shadow {
        box-shadow: rgba(0, 0, 0, 0.24) 0px 3px 8px;
    }
    .job-title-container {
        padding: 1rem 2rem 1rem 2rem;
        border-bottom: 1px solid #bfd1ff;
    }
    .job-steps-container {
        display: flex;
        height: 60%;
    }
    .job-steps-container div {
        flex-basis: 25%;
        flex-grow: 1;
    }
`

const JobStepsContainer = (props: {
    currentStep: number;
    completedStep: number;
    children: React.ReactNode;
    handleNextClick: () => void;
    handlePrevClick: () => void;
    hideMakeAPitch?: boolean;
    submitBtnTitle?: string;
    changeStep?: (stepIdx: number) => void;
    loadingStep?: number;
}) => {

    const currentTrack = useSelector((state: RootState) => state.evaluationPlatform.currentTrack?.candidateTrack?.[0]);

    const handleChangeStep = (step: number) => {
        props.changeStep && props.changeStep(step);
    }

    return (
        <StyledWrapper>
            <div className="steps-container styled-box-shadow">
                <div className="job-title-container text-left">
                    <h3>{currentTrack?.title}</h3>
                </div>
                <div className="job-steps-container">
                    <StepViewer
                        selected={props.currentStep === 0}
                        title="Resume_JD.pdf"
                        stepName={'Step 1'}
                        completed={props.completedStep >= 0}
                        icon={FilePerson}
                        onClick={() => handleChangeStep(0)}
                        allowClick={props.completedStep + 1 >= 0}
                        loading={props.loadingStep === 0}
                    />
                    <StepViewer
                        selected={props.currentStep === 1}
                        title="Build your Profile"
                        stepName={'Step 2'}
                        completed={props.completedStep >= 1}
                        icon={AssignmentInd}
                        onClick={() => handleChangeStep(1)}
                        allowClick={props.completedStep + 1 >= 1}
                        loading={props.loadingStep === 1}
                    />
                    <StepViewer
                        selected={props.currentStep === 2}
                        title="Add your Experience & Expertise"
                        stepName={'Step 3'}
                        completed={props.completedStep >= 2}
                        icon={BadgeCheck}
                        onClick={() => handleChangeStep(2)}
                        allowClick={props.completedStep + 1 >= 2}
                        loading={props.loadingStep === 2}
                    />
                    {!props.hideMakeAPitch && <StepViewer
                        selected={props.currentStep === 3}
                        title="Make a Pitch"
                        stepName={'Step 4'}
                        completed={props.completedStep >= 3}
                        icon={Microphone}
                        onClick={() => handleChangeStep(3)}
                        allowClick={props.completedStep + 1 >= 3}
                        loading={props.loadingStep === 3}
                    />}
                </div>
            </div>
            <div className="steps-form-container styled-box-shadow" id="onboard-content-form-id">
                <div>
                    {props.children}
                </div>
            </div>
            <div className="px-4 d-flex align-items-center justify-content-end">
                <Button
                    variant="outlined"
                    color="primary"
                    className="mr-4"
                    type={"button"}
                    onClick={props.handlePrevClick}>
                    <LeftArrowAlt width={'25px'} className="mr-2" />
                    Previous
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    type="button"
                    onClick={props.handleNextClick}>
                    {props.submitBtnTitle ? props.submitBtnTitle :
                        <>
                            Save & Next
                            <RightArrowAlt width={'25px'} className="ml-2" />
                        </>}
                </Button>
            </div>
        </StyledWrapper>
    )
}

const StyledStepsWrapper = styled.div<{ selected: boolean, allowClick: boolean }>`
    .step-name-style {
        display: inline-block;
        margin-left: 0.2rem;
    }
    padding: 1rem 2rem 1rem 2rem !important;
    background: ${props => props.selected ? "#eaefff" : '#ffffff'};
    
    cursor: ${props => (props.allowClick ? "pointer" : "not-allowed")};
    &:hover {
        background: ${props => (props.selected ? "#d0d8ff" : "#f9f9f9")};
        box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    }
`
const StepViewer = (props: {
    selected: boolean;
    title: string;
    completed: boolean;
    allowClick: boolean;
    icon: StyledIcon;
    stepName: string;
    onClick: () => void;
    loading: boolean;
}) => {
    return (
        <StyledStepsWrapper
            className="text-left"
            selected={props.selected}
            allowClick={props.allowClick}
            onClick={() => {
                props.allowClick && props.onClick();
            }}
        >
            <span className="text-muted step-name-style">{props.stepName}</span>
            <div className="mt-2 d-flex align-items-center justify-content-between">
                <div className="flex-grow-1">
                    <props.icon width={'30px'} color="#325cd5" />
                    <span className="ml-2">{props.title}</span>
                </div>
                {props.loading ? <span><BounceLoader loading={props.loading} size={20} color='#315CD5' css={`margin: auto`} /> <div>...Saving</div></span>
                    : props.completed && <CheckCircleFill width={'15px'} color="#2bba2f" style={{ flexBasis: '15px' }} />}
            </div>
        </StyledStepsWrapper>
    )
}

export default JobStepsContainer;